<template>
  <form @submit.prevent="emit('request', selectedLogType, targetLocation, timeWindow || undefined)" class="space-y-5">
    <InfuseField :label="t('form.log.type')" size="small">
      <InfuseSelect
        v-model="selectedLogType"
        name="select-filter"
        size="small"
        :options="logTypes"
        :disabled="props.protocol === Protocol.OCPPJ15 || props.protocol === Protocol.OCPPJ16"
        required
      />
    </InfuseField>

    <InfuseField v-if="featureStore.hasFeature(Feature.DIAGNOSTICS_RETRIEVAL)" size="small">
      <label class="flex items-center gap-2">
        <InfuseCheckBox v-model="useCustomUrl" />
        {{ t('cpms.diagnostics.action.use-custom-url.label') }}
      </label>
    </InfuseField>

    <InfuseField
      v-if="!featureStore.hasFeature(Feature.DIAGNOSTICS_RETRIEVAL) || useCustomUrl"
      :label="t('form.log.targetLocation.label')"
      size="small"
    >
      <InfuseInput
        v-model="targetLocation"
        type="url"
        size="small"
        :placeholder="t('form.log.targetLocation.placeholder')"
        autofocus
        required
      />
    </InfuseField>

    <InfuseField :label="t('form.log.timeWindow')" size="small" optional>
      <InfuseDatePicker v-model="timeWindow" range />
    </InfuseField>

    <InfuseButton
      data-testid="page-request-diagnostics-button"
      v-permission="{ id: commandPermissions.GET_LOG, hide: false }"
      class="w-full md:w-48"
      type="submit"
      :disabled="(urlRequired && !targetLocation) || !selectedLogType"
    >
      {{ t('action.requestDiagnostics') }}
    </InfuseButton>
  </form>
</template>

<script setup lang="ts">
import { commandPermissions, LogType } from '@infuse/api/cpms/operator';
import { Protocol } from '@infuse/cpms/charging-station/store';
import {
  InfuseButton,
  InfuseCheckBox,
  InfuseDatePicker,
  InfuseField,
  InfuseInput,
  InfuseSelect
} from '@infuse/ui/components';

import { vPermission } from '@infuse/iam/permission/directives';
import '@vuepic/vue-datepicker/dist/main.css';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Feature, useFeatureStore } from '@infuse/cpms/shared/features/store';

const { t } = useI18n();
const featureStore = useFeatureStore();
const useCustomUrl = ref(false);

watch(useCustomUrl, () => {
  resetLocation();
});

interface Emits {
  (e: 'request', type: LogType, targetLocation?: string, timeWindow?: [Date, Date]): void;
}
const emit = defineEmits<Emits>();

interface Props {
  protocol?: Protocol;
}
const props = withDefaults(defineProps<Props>(), {
  protocol: Protocol.OCPPJ16
});

const protocol = computed(() => props.protocol);

const urlRequired = computed(() =>
  !featureStore.hasFeature(Feature.DIAGNOSTICS_RETRIEVAL) || useCustomUrl.value
);

const timeWindow = ref<[Date, Date] | null>(null);
const targetLocation = ref<string | undefined>(undefined);
const selectedLogType = ref<LogType>(LogType.DIAGNOSTICS_LOG);
const logTypes = ref<{ key: string; value: LogType }>([]);

watch(
  protocol,
  (protocol) => {
    if (protocol) {
      reset();
      updateLogTypes();
    }
  },
  { immediate: true }
);

async function reset() {
  timeWindow.value = null;
  selectedLogType.value = LogType.DIAGNOSTICS_LOG;
  await resetLocation();
}

async function resetLocation() {
  targetLocation.value = undefined;
}

async function updateLogTypes() {
  switch (props.protocol) {
    case Protocol.OCPP21:
      logTypes.value = Object.values(LogType).map((logType) => ({
        key: t(`logType.${logType}`),
        value: logType
      }));
      break;
    case Protocol.OCPP201:
      logTypes.value = [
        {
          key: t(`logType.${LogType.DIAGNOSTICS_LOG}`),
          value: LogType.DIAGNOSTICS_LOG
        },
        {
          key: t(`logType.${LogType.SECURITY_LOG}`),
          value: LogType.SECURITY_LOG
        }
      ];
      break;
    default:
      logTypes.value = [
        {
          key: t(`logType.${LogType.DIAGNOSTICS_LOG}`),
          value: LogType.DIAGNOSTICS_LOG
        }
      ];
      break;
  }
}
</script>

<i18n>
{
  "en": {
    "action": {
      "requestDiagnostics": "Send request"
    },
    "targetLocation": {
      "placeholder": "FTP upload URL"
    },
    "logType": {
      "DATA_COLLECTOR_LOG": "Data collector",
      "DIAGNOSTICS_LOG": "Diagnostics",
      "SECURITY_LOG": "Security"
    },
    "form": {
      "log": {
        "type": "Log type",
        "targetLocation": {
          "label": "FTP upload URL",
          "placeholder": "ftp://server/pathname"
        },
        "timeWindow": "Start and end date"
      }
    }
  },
  "nl": {
    "action": {
      "requestDiagnostics": "Stuur verzoek"
    },
    "targetLocation": {
      "placeholder": "FTP-URL om naar te uploaden"
    },
    "logType": {
      "DATA_COLLECTOR_LOG": "Data collector",
      "DIAGNOSTICS_LOG": "Diagnostics",
      "SECURITY_LOG": "Security"
    },
    "form": {
      "log": {
        "type": "Log type",
        "targetLocation": {
          "label": "FTP upload URL",
          "placeholder": "ftp://server/pad"
        },
        "timeWindow": "Start- en einddatum"
      }
    }
  }
}
</i18n>
